import React, { useEffect, useState } from "react";
import { Check, X, Eye, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { availabilityApi } from "../../services/availabilityApi";
import { employeeApi } from "../../services/employeeApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { usePermissions } from "../../hooks/usePermissions";
import { AvailabilityDecisionDialog } from "../../components/availability/AvailabilityDecisionDialog";
import { statusLabel } from "../../lib/statusLabel";
import { useLocalization } from "../../hooks/useLocalization";

export default function AvailabilityReviewPage() {
  const { t } = useLocalization();
  const { canReviewAvailability } = usePermissions();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");
  const [error, setError] = useState("");
  const [decisionTarget, setDecisionTarget] = useState(null);
  const [decisionMode, setDecisionMode] = useState(null);

  const load = () => availabilityApi.listAll(null, null, statusFilter || null).then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    if (!canReviewAvailability) return;
    load();
    employeeApi.listSchedulable().then((r) => setEmployees(r.data)).catch(() => {});
  }, [canReviewAvailability, statusFilter]);

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || t("mgmtAvailability.employeeOtherStore");

  const act = async (fn, msg) => {
    setError("");
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      setError(detail);
      toast.error(detail);
      throw err;
    }
  };

  const openDecision = (request, mode) => { setDecisionTarget(request); setDecisionMode(mode); };
  const decide = (id, decision, note) => act(() => availabilityApi.review(id, decision, note), decision === "APPROVED" ? t("mgmtAvailability.approvedToast") : decision === "PARTIALLY_APPROVED" ? t("mgmtAvailability.approvedWithConditionsToast") : t("mgmtAvailability.declinedToast"));
  const clarify = (id, message) => act(() => availabilityApi.requestClarification(id, message), t("mgmtAvailability.clarificationRequestedToast"));

  if (!canReviewAvailability) {
    return <p className="text-sm text-gray-500" data-testid="availability-review-access-denied">{t("common.accessDenied")}</p>;
  }

  return (
    <div className="space-y-4" data-testid="availability-review-page">
      <PageHelpBanner pageKey="availability-review" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtAvailability.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtAvailability.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtAvailability.subtitle")}</p>
      </div>

      <div className="flex gap-2">
        {["SUBMITTED", "UNDER_REVIEW", "APPROVED", "PARTIALLY_APPROVED", "DECLINED", ""].map((s) => (
          <button
            key={s || "ALL"}
            data-testid={`availability-status-filter-${s || "ALL"}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
          >
            {s ? statusLabel(t, s) : t("common.all")}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-500" data-testid="availability-review-error">{error}</p>}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtAvailability.colEmployee")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtAvailability.colType")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtAvailability.colMonth")}</th>
              <th className="px-4 py-3 font-medium">{t("common.date")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtAvailability.colDetail")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`availability-review-row-${r.id}`}>
                <td className="px-4 py-3 text-xs text-gray-700 font-medium">{employeeName(r.employee_id)}</td>
                <td className="px-4 py-3">{r.request_type === "TIME_OFF" ? t("mgmtAvailability.dayOff") : t("mgmtAvailability.availability")}</td>
                <td className="px-4 py-3">{r.target_month}</td>
                <td className="px-4 py-3">{r.date || "-"}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{r.availability_type || r.notes || "-"}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {r.status === "SUBMITTED" && (
                      <button data-testid={`start-review-${r.id}`} onClick={() => act(() => availabilityApi.startReview(r.id), t("mgmtAvailability.underReviewToast"))} className="text-gray-500 hover:text-blue-600" title={t("mgmtAvailability.startReviewTitle")}><Eye size={14} /></button>
                    )}
                    {["SUBMITTED", "UNDER_REVIEW"].includes(r.status) && (
                      <>
                        <button data-testid={`approve-availability-${r.id}`} onClick={() => openDecision(r, "APPROVE")} className="text-emerald-600 hover:text-emerald-800" title={t("common.approve")}><Check size={14} /></button>
                        <button data-testid={`partial-approve-availability-${r.id}`} onClick={() => openDecision(r, "APPROVE_WITH_CONDITIONS")} className="text-amber-600 hover:text-amber-800 text-[10px] font-semibold border border-amber-300 rounded px-1" title={t("mgmtAvailability.approveWithConditionsTitle")}>1/2</button>
                        <button data-testid={`clarify-availability-${r.id}`} onClick={() => openDecision(r, "CLARIFY")} className="text-blue-500 hover:text-blue-700" title={t("mgmtAvailability.requestClarificationTitle")}><HelpCircle size={14} /></button>
                        <button data-testid={`decline-availability-${r.id}`} onClick={() => openDecision(r, "DECLINE")} className="text-red-500 hover:text-red-700" title={t("mgmtAvailability.declineTitle")}><X size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="no-availability-review-rows">{t("mgmtAvailability.noRequests")}</td></tr>}
          </tbody>
        </table>
      </div>
      <AvailabilityDecisionDialog
        open={!!decisionTarget} onOpenChange={(next) => !next && setDecisionTarget(null)}
        mode={decisionMode} request={decisionTarget} onDecide={decide} onClarify={clarify}
      />
    </div>
  );
}
